'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { css } from 'styled-components';

import { ImageSlide } from '@/app/components/hotel-detail/HeroGalleryImageSlide';
import { getPhotoAlt } from '@/app/components/hotel-detail/getPhotoAlt';

import {
  getGallerySourceOptimization,
  getHeroImageSourcePreload,
} from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { useIsDesktopOrGreater, useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { HeroNoImageAvailable } from '@/app/components/hotel-detail/HeroNoImageAvailable';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useBrowserBackButton } from '@/app/components/hotel-detail/hooks/useBrowserBackButton';
import { TrustYouWidget } from '@/app/components/hotel-detail/trust-you/TrustYouWidget';
import ImageGalleryPageLayout from '@/app/components/hotel-detail/ImageGalleryPageLayout';
import ShareButton from '@/app/components/hotel-detail/ShareButton';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { BackArrow, BackButtonWithArrow } from '@/app/components/hotel-detail/BackButton';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isTrustYouSource } from '@/app/common/reviews/reviewSource';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { URLParameters } from '@/app/common/urlParameters';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useTranslation } from '@/app/components/translations/Translation';
import { createDetailPageImageGalleryClickedPayload } from '@/app/common/starTrack/events/detailPageImageGalleryClicked.ts';
import { useFetchHotelStatic, useIsHotelStaticLoading } from './hooks/useFetchHotelStatic';
import { useHeroStrip } from './hooks/useHeroStrip';
import HeroGallerySliderOverlay from './HeroGallerySliderOverlay';
import { useMetaReview } from './hooks/useMetaReview';

export type HeroGalleryProps = {
  images?: string[];
  name: string;
  isEvo?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const MoreImagesOverlay = dynamic(
  () => import(/* webpackChunkName: "HeroGalleryDesktopOverlay" */ './HeroGalleryMoreImagesOverlay'),
  {
    ssr: false,
  }
);

type OverlayStateType = { isOpen: boolean | undefined; initialFocussedImageIndex?: number | undefined };

/**
 * Basic cross device functionality for the gallery shown in hotel page hero.
 *
 * Renders the common parts needed for all device sizes on server side.
 * Those are basically just a list of the first 5 images that is scrollable on small devices
 * and layouted in a grid on larger screens.
 *
 * Screensize specific functionality is conditionally added or loaded on the client only at runtime.
 *
 * Adds preload of the main image to improve LCP, see:
 * - https://web.dev/preload-responsive-images/
 * - uses link imagesrcset attribute to match the same image that will be loaded in the browser
 *
 */
export default function HeroGallery(props: HeroGalleryProps) {
  const { images, name, isEvo } = props;
  const { sendCuriosityClickEvent } = useCuriosity();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const isHotelStaticLoading = useIsHotelStaticLoading();
  const { commonData, localization } = usePageWrapperData();
  const { isApp } = commonData;
  const { reviewsDisabled } = localization.configValuesApp;
  // Only fetch the TrustYou meta-review for an explicit TrustYou hotel (not TA/unknown/absent/Expedia).
  const isTrustYou = isTrustYouSource(hotelStaticData?.hotelOpinion.reviewSource);
  const { metaReview } = useMetaReview(
    isTrustYou && !reviewsDisabled ? hotelStaticData?.hotelOpinion.opinionId : undefined,
    localization.lang
  );
  const { starTrack } = useCommonClient();
  const initialParams = useInitialParams();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const { t } = useTranslation();

  const isDesktopOrGreater = useIsDesktopOrGreater();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const [overlayState, setOverlayState] = useState<OverlayStateType>({ isOpen: undefined });
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [trustYouWidgetOpen, setTrustYouWidgetOpen] = useState<boolean>(true);

  const firstImageRef = useRef<HTMLDivElement>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  const mouseenterHandler = useCallback(() => {
    setTrustYouWidgetOpen(false);
  }, []);

  const mouseleaveHandler = useCallback(() => {
    setTrustYouWidgetOpen(true);
  }, []);

  useEffect(() => {
    const firstImage = firstImageRef?.current || null;

    if (!firstImage || !isDesktopOrGreater) return;

    firstImage.addEventListener('mouseenter', mouseenterHandler);
    firstImage.addEventListener('mouseleave', mouseleaveHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      firstImage.removeEventListener('mouseenter', mouseenterHandler);
      firstImage.removeEventListener('mouseleave', mouseleaveHandler);
    };
  }, [isDesktopOrGreater, mouseenterHandler, mouseleaveHandler]);

  useBrowserBackButton({
    condition: overlayState.isOpen,
    modalId: 'hotel-gallery',
    onOpen: () =>
      setOverlayState({
        isOpen: true,
        initialFocussedImageIndex: currentImageIndex,
      }),
    onClose: () => setOverlayState({ isOpen: false }),
  });

  // Tablet and desktop lay the first 5 out in a grid, the last one pointing at the rest; the mobile
  // strip swipes through every photo instead, and loops both ways (MPH-1213, MPH-1237).
  const isMobileLayout = isMobile || (isMobile === undefined && commonData.isMobileDevice);

  const initialImages = isMobileLayout ? images : images?.slice(0, 5);

  const initialImagesWithSources = useMemo(
    () =>
      getGallerySourceOptimization({
        images: initialImages || [''],
        isMobile,
        isTopSecret: false,
        totalImages: initialImages?.length || 0,
      }),
    [images, isMobile]
  );

  const strip = useHeroStrip(sliderContainerRef, initialImagesWithSources.length, Boolean(isMobile));

  const preloadImages = initialImages
    ? getHeroImageSourcePreload({
        url: initialImages[0],
        isTopSecret: false,
        totalImages: images?.length || 0,
        isMobile,
      })
    : null;

  if (!images?.length) return <HeroNoImageAvailable />;

  const imageAmount = initialImages?.length || 0;

  async function onImageClick(index: number, hasOverlay: boolean) {
    starTrack?.detailPageImageGalleryClicked(
      createDetailPageImageGalleryClickedPayload({
        searchId: searchId || '',
        productDetailKey: initialParams.productDetailKey,
      })
    );
    if (hasOverlay) {
      sendCuriosityClickEvent({
        event_name: 'click_gallery_show_all_overlay',
        event_context: 'hero_gallery',
      });
    } else {
      sendCuriosityClickEvent({
        event_name: 'click_gallery_hero_image',
        event_context: 'hero_gallery',
        event_value: `image_num_${index}`,
      });
    }
    setCurrentImageIndex(index);
    setOverlayState({ isOpen: true, initialFocussedImageIndex: index });
  }

  const showBackArrow = !isApp && !initialParams.isHotelStaticPage && isMobile;

  const reviewCount = metaReview?.reviewCount || hotelStaticData?.hotelOpinion.numberOfReviews;
  const reviewScore = metaReview?.score || hotelStaticData?.hotelOpinion.rating;

  return (
    <>
      {/*
        React hoists these into <head> on its own, including during the server render, which is the
        only place a preload can help LCP. They used to go through `next/head` — a Pages Router api
        that does nothing under the App Router, so the hero preload had silently stopped emitting.
      */}
      {preloadImages?.map((preloadImage) => (
        // Keyed by media as well: a Booking url without `/max500/` gives every breakpoint the same
        // href, and a key on `href` alone would then repeat (MPH-628).
        <link
          key={`${preloadImage.media ?? ''} ${preloadImage.href}`}
          rel="preload"
          as="image"
          href={preloadImage.href}
          imageSrcSet={preloadImage.imageSrcset}
          media={preloadImage.media}
          fetchPriority="high"
        />
      ))}

      <div
        css={css`
          overflow-x: auto;
          position: relative;
        `}
      >
        <div
          ref={sliderContainerRef}
          css={css`
            position: relative;
            height: 260px;
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;

            &::scrollbar {
              display: none;
            }

            &::-webkit-scrollbar {
              display: none;
            }

            @media ${customCss.breakpoints.sm} {
              display: grid;
              grid-gap: 8px;
              z-index: 0;
              padding: 0;
              ${
                imageAmount === 5
                  ? `
                  grid-template-columns: 2fr 1fr 1fr;
                  grid-template-rows: 1fr 1fr;

                  & > *:first-child {
                    grid-row: 1 / 3;
                  }
                `
                  : imageAmount === 4
                    ? `
                  grid-template-columns: 2fr 1fr 1fr;
                  grid-template-rows: 1fr 1fr;

                  & > *:first-child,
                  & > *:nth-child(2) {
                    grid-row: 1 / 3;
                  }
                `
                    : imageAmount === 3
                      ? `
                  grid-template-columns: 2fr 1fr;
                  grid-template-rows: 1fr 1fr;

                  & > *:first-child {
                    grid-row: 1 / 3;
                  }
                `
                      : imageAmount === 2
                        ? `
                  grid-template-columns: 2fr 1fr;
                `
                        : ``
              }
              & > * {
                max-height: 100%;
                overflow: hidden;
              }
            }

            @media ${customCss.breakpoints.sm} {
              height: 300px;
            }

            @media ${customCss.breakpoints.xl} {
              height: 400px;
            }
          `}
        >
          {strip.hasLeadingCopy && (
            // Swiping back onto this copy of the last photo goes on to the real one (MPH-1237).
            <ImageSlide
              key="loop-copy-start"
              {...strip.getLoopCopy('start', initialImagesWithSources, (index) => onImageClick(index, false))}
              css={`
                flex: 0 0 100%;
              `}
            />
          )}
          {initialImagesWithSources.map((image, index) => (
            <ImageSlide
              containerRef={index === 0 ? firstImageRef : undefined}
              {...strip.getSlideImage(index, image)}
              /**
               * Keyed by position, not by url. The hotel's own photos replace the ones borrowed
               * from the results card, and a url key made every slide a different element, so the
               * swap remounted the strip and threw the reader back to the first photo. Keying the
               * slot keeps the element — and the scroll position — so you stay where you were and
               * that slot simply shows the photo the hotel's list has there (MPH-1002).
               */
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              role="button"
              aria-label={getPhotoAlt(t, name, index, images.length)}
              {...strip.getSlideAccessibility(index)}
              /*
               * The first slide is the hero, so it is never lazy: it is what the preload in the
               * head is for, and `loading="lazy"` on the LCP image undoes that. `!isMobile` used to
               * catch desktop and the server render alike, where it is the only slide there is.
               * On the mobile strip the photos within reach load right away, and the rest are
               * placeholders until the reader gets near them.
               */
              loading={index === 0 || (isMobile && strip.isPhotoReady(index)) ? 'eager' : 'lazy'}
              onKeyDown={strip.onSlideKeyDown(index)}
              onClick={() => {
                onImageClick(
                  index,
                  index === initialImagesWithSources.length - 1 && images.length > initialImagesWithSources.length
                );
              }}
              onKeyPress={() => {
                onImageClick(
                  index,
                  index === initialImagesWithSources.length - 1 && images.length > initialImagesWithSources.length
                );
              }}
              overlay={
                index === initialImagesWithSources.length - 1 && images.length > initialImagesWithSources.length ? (
                  <MoreImagesOverlay
                    imageAmount={images.length}
                    css={`
                      position: absolute;
                      left: 0;
                      width: 100%;
                      top: 0;
                      height: 100%;
                      @media ${customCss.breakpoints.sm} {
                        border-radius: 8px;
                      }
                    `}
                  />
                ) : undefined
              }
              css={`
                flex: 0 0 100%;
              `}
            />
          ))}
          {strip.loop && (
            // Swiping onto this copy of the first photo goes back to the real one (MPH-1213).
            <ImageSlide
              key="loop-copy"
              {...strip.getLoopCopy('end', initialImagesWithSources, (index) => onImageClick(index, false))}
              css={`
                flex: 0 0 100%;
              `}
            />
          )}
        </div>
        {isMobile && !initialParams.isHotelStaticPage ? (
          <div
            css={css`
              position: absolute;
              top: 0;
              right: 0;
              padding: 16px;
            `}
          >
            <ShareButton hotelName={name} className="info-share" style={{ gridArea: 'share' }} />
          </div>
        ) : null}
        {showBackArrow && (
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              padding: 16px;
            `}
          >
            <BackButtonWithArrow
              solidIcon={true}
              css={css`
                ${BackArrow} {
                  display: flex;
                  justify-content: center;
                  border-radius: 50%;
                  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
                  width: 32px;
                  height: 32px;

                  & svg,
                  & [data-library-icon] {
                    width: 16px;
                    height: 16px;
                  }
                }
              `}
            />
          </div>
        )}
        {/* The widget self-guards: TA renders from the rating, TY needs its key, any other source renders nothing. */}
        {isDesktopOrGreater && !isEvo && !reviewsDisabled && hotelStaticData?.hotelOpinion ? (
          <TrustYouWidget
            css={css`
              position: absolute;
              left: 0;
              top: 0;
            `}
            rating={reviewScore}
            reviews={reviewCount}
            trustYouId={hotelStaticData.hotelOpinion.opinionId}
            opinionCategories={hotelStaticData.hotelOpinion.categories}
            reviewSource={hotelStaticData.hotelOpinion.reviewSource}
            seeMoreLink="reviews"
            isOpen={trustYouWidgetOpen}
          />
        ) : undefined}
        {isMobile && images.length ? (
          <HeroGallerySliderOverlay
            initialImageIndex={0}
            keepScrollPosition={true}
            scrollContainerRef={sliderContainerRef}
            amountOfSlides={initialImagesWithSources.length}
            counterTotalSlides={images ? images.length : 0}
            slideChanged={strip.onSlideChanged}
            hasLeadingCopy={strip.hasLeadingCopy}
            onCounterClick={(slideIndex) => onImageClick(slideIndex, false)}
            /*
             * Held back until the request settles, on the same flag the hero's other sections use.
             * The count is right either way, but until then it is the few photos borrowed from the
             * results card ("1 / 5") and would flip to the hotel's real total ("1 / 52") under the
             * reader a moment later.
             *
             * Once it has settled the counter shows, however it settled: on a failure the strip
             * keeps the borrowed photos and the borrowed count describes them correctly (MPH-1002).
             */
            counterDisplay={isHotelStaticLoading ? 'none' : 'default'}
            showCounterIcon={true}
            hidePagination={true}
            counterPosition={`
              bottom: 16px;
              right: 16px;
            `}
          />
        ) : undefined}
      </div>

      <ImageGalleryPageLayout
        images={images}
        initialImageIndex={overlayState.initialFocussedImageIndex}
        name={name}
        isOpen={overlayState.isOpen}
        onDismiss={() => setOverlayState({ isOpen: false })}
        navigateToRooms={!initialParams.isHotelStaticPage}
      />
    </>
  );
}
